import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getLocalizedField, getImageUrl } from '@/utils/helpers';
import { Search, Package, Palette, UtensilsCrossed, MessageSquare, User, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/search/global`, {
        params: { q: searchQuery, limit: 20 }
      });
      setResults(res.data);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  const getTotalCount = () => {
    if (!results) return 0;
    return (
      results.products?.length +
      results.artworks?.length +
      results.recipes?.length +
      results.posts?.length +
      results.creators?.length
    ) || 0;
  };

  return (
    <div className="min-h-screen bg-amber-50/50">
      {/* 搜索头部 */}
      <div className="bg-amber-50 border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-6">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-500" />
                <Input
                  type="text"
                  placeholder={t('search.placeholder', '搜索产品、作品、食谱、帖子、创作者...')}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-10 h-12 text-lg"
                  data-testid="global-search-input"
                />
              </div>
              <Button type="submit" size="lg" className="bg-amber-600 hover:bg-amber-700" data-testid="search-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('search.search', '搜索')}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* 搜索结果 */}
      <div className="container mx-auto px-4 py-8">
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
          </div>
        )}

        {results && !loading && (
          <>
            <div className="mb-6">
              <h2 className="text-xl font-semibold">
                {t('search.resultsFor', '搜索结果')}: "{results.query}"
                <span className="text-amber-600 font-normal ml-2">
                  ({getTotalCount()} {t('search.results', '个结果')})
                </span>
              </h2>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-6 flex-wrap h-auto gap-2">
                <TabsTrigger value="all" className="gap-2">
                  {t('search.all', '全部')} ({getTotalCount()})
                </TabsTrigger>
                <TabsTrigger value="products" className="gap-2">
                  <Package className="w-4 h-4" />
                  {t('search.products', '产品')} ({results.products?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="artworks" className="gap-2">
                  <Palette className="w-4 h-4" />
                  {t('search.artworks', '作品')} ({results.artworks?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="recipes" className="gap-2">
                  <UtensilsCrossed className="w-4 h-4" />
                  {t('search.recipes', '食谱')} ({results.recipes?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="posts" className="gap-2">
                  <MessageSquare className="w-4 h-4" />
                  {t('search.posts', '帖子')} ({results.posts?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="creators" className="gap-2">
                  <User className="w-4 h-4" />
                  {t('search.creators', '创作者')} ({results.creators?.length || 0})
                </TabsTrigger>
              </TabsList>

              {/* 全部结果 */}
              <TabsContent value="all">
                <div className="space-y-8">
                  {results.products?.length > 0 && (
                    <ResultSection
                      title={t('search.products', '产品')}
                      icon={<Package className="w-5 h-5" />}
                      items={results.products}
                      renderItem={(item) => (
                        <ProductCard key={item.id} product={item} navigate={navigate} i18n={i18n} />
                      )}
                    />
                  )}
                  {results.artworks?.length > 0 && (
                    <ResultSection
                      title={t('search.artworks', '作品')}
                      icon={<Palette className="w-5 h-5" />}
                      items={results.artworks}
                      renderItem={(item) => (
                        <ArtworkCard key={item.id} artwork={item} navigate={navigate} i18n={i18n} />
                      )}
                    />
                  )}
                  {results.recipes?.length > 0 && (
                    <ResultSection
                      title={t('search.recipes', '食谱')}
                      icon={<UtensilsCrossed className="w-5 h-5" />}
                      items={results.recipes}
                      renderItem={(item) => (
                        <RecipeCard key={item.id} recipe={item} navigate={navigate} i18n={i18n} />
                      )}
                    />
                  )}
                  {results.posts?.length > 0 && (
                    <ResultSection
                      title={t('search.posts', '帖子')}
                      icon={<MessageSquare className="w-5 h-5" />}
                      items={results.posts}
                      renderItem={(item) => (
                        <PostCard key={item.id} post={item} navigate={navigate} />
                      )}
                    />
                  )}
                  {results.creators?.length > 0 && (
                    <ResultSection
                      title={t('search.creators', '创作者')}
                      icon={<User className="w-5 h-5" />}
                      items={results.creators}
                      renderItem={(item) => (
                        <CreatorCard key={item.id} creator={item} navigate={navigate} />
                      )}
                    />
                  )}
                  {getTotalCount() === 0 && (
                    <div className="text-center py-12 text-amber-600">
                      <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{t('search.noResults', '没有找到相关结果')}</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* 产品结果 */}
              <TabsContent value="products">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {results.products?.map((item) => (
                    <ProductCard key={item.id} product={item} navigate={navigate} i18n={i18n} />
                  ))}
                </div>
                {results.products?.length === 0 && <NoResults />}
              </TabsContent>

              {/* 作品结果 */}
              <TabsContent value="artworks">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {results.artworks?.map((item) => (
                    <ArtworkCard key={item.id} artwork={item} navigate={navigate} i18n={i18n} />
                  ))}
                </div>
                {results.artworks?.length === 0 && <NoResults />}
              </TabsContent>

              {/* 食谱结果 */}
              <TabsContent value="recipes">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {results.recipes?.map((item) => (
                    <RecipeCard key={item.id} recipe={item} navigate={navigate} i18n={i18n} />
                  ))}
                </div>
                {results.recipes?.length === 0 && <NoResults />}
              </TabsContent>

              {/* 帖子结果 */}
              <TabsContent value="posts">
                <div className="space-y-4">
                  {results.posts?.map((item) => (
                    <PostCard key={item.id} post={item} navigate={navigate} />
                  ))}
                </div>
                {results.posts?.length === 0 && <NoResults />}
              </TabsContent>

              {/* 创作者结果 */}
              <TabsContent value="creators">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {results.creators?.map((item) => (
                    <CreatorCard key={item.id} creator={item} navigate={navigate} />
                  ))}
                </div>
                {results.creators?.length === 0 && <NoResults />}
              </TabsContent>
            </Tabs>
          </>
        )}

        {!results && !loading && (
          <div className="text-center py-12 text-amber-600">
            <Search className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-xl mb-2">{t('search.startSearch', '开始搜索')}</h3>
            <p>{t('search.startSearchDesc', '输入关键词搜索产品、作品、食谱、帖子或创作者')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// 结果分区组件
const ResultSection = ({ title, icon, items, renderItem }) => (
  <div>
    <h3 className="flex items-center gap-2 text-lg font-semibold mb-4">
      {icon} {title}
    </h3>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.slice(0, 4).map(renderItem)}
    </div>
  </div>
);

// 无结果组件
const NoResults = () => {
  const { t } = useTranslation();
  return (
    <div className="text-center py-12 text-amber-600">
      <p>{t('search.noResults', '没有找到相关结果')}</p>
    </div>
  );
};

// 产品卡片
const ProductCard = ({ product, navigate, i18n }) => (
  <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/product/${product.id}`)}>
    <div className="aspect-square overflow-hidden">
      <img src={getImageUrl(product.image_url)} alt={getLocalizedField(product, 'name', i18n.language)} className="w-full h-full object-cover" />
    </div>
    <CardContent className="p-3">
      <h4 className="font-medium truncate">{getLocalizedField(product, 'name', i18n.language)}</h4>
      <p className="text-amber-600 font-semibold">¥{product.price}</p>
      {product.category && <Badge variant="outline" className="mt-1">{product.category}</Badge>}
    </CardContent>
  </Card>
);

// 作品卡片
const ArtworkCard = ({ artwork, navigate, i18n }) => (
  <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/artwork/${artwork.id}`)}>
    <div className="aspect-square overflow-hidden">
      <img src={getImageUrl(artwork.image_url)} alt={getLocalizedField(artwork, 'title', i18n.language)} className="w-full h-full object-cover" />
    </div>
    <CardContent className="p-3">
      <h4 className="font-medium truncate">{getLocalizedField(artwork, 'title', i18n.language)}</h4>
      <p className="text-sm text-amber-600 truncate">{getLocalizedField(artwork, 'artist', i18n.language)}</p>
      {artwork.is_original_for_sale && (
        <Badge className="mt-1 bg-amber-500">{artwork.original_price ? `¥${artwork.original_price}` : '出售中'}</Badge>
      )}
    </CardContent>
  </Card>
);

// 食谱卡片
const RecipeCard = ({ recipe, navigate, i18n }) => (
  <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/recipe/${recipe.id}`)}>
    <div className="aspect-square overflow-hidden">
      <img src={getImageUrl(recipe.image_url)} alt={getLocalizedField(recipe, 'name', i18n.language)} className="w-full h-full object-cover" />
    </div>
    <CardContent className="p-3">
      <h4 className="font-medium truncate">{getLocalizedField(recipe, 'name', i18n.language)}</h4>
      <div className="flex items-center gap-2 text-sm text-amber-600 mt-1">
        {recipe.prep_time && <span>⏱ {recipe.prep_time}分钟</span>}
        {recipe.difficulty && <Badge variant="outline">{recipe.difficulty}</Badge>}
      </div>
    </CardContent>
  </Card>
);

// 帖子卡片
const PostCard = ({ post, navigate }) => (
  <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/community/post/${post.id}`)}>
    <CardContent className="p-4">
      <div className="flex gap-4">
        {post.images?.[0] && (
          <div className="w-20 h-20 flex-shrink-0 overflow-hidden rounded">
            <img src={getImageUrl(post.images[0])} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="line-clamp-2">{post.content}</p>
          <div className="flex items-center gap-4 text-sm text-amber-600 mt-2">
            <span>❤️ {post.likes_count || 0}</span>
            <span>💬 {post.comments_count || 0}</span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// 创作者卡片
const CreatorCard = ({ creator, navigate }) => (
  <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/profile/${creator.id}`)}>
    <CardContent className="p-4 text-center">
      <div className="w-20 h-20 mx-auto mb-3 rounded-full overflow-hidden bg-amber-100">
        {creator.avatar ? (
          <img src={getImageUrl(creator.avatar)} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-2xl text-amber-500">
            <User className="w-10 h-10" />
          </div>
        )}
      </div>
      <h4 className="font-medium">{creator.name}</h4>
      {creator.bio && (
        <p className="text-sm text-amber-600 line-clamp-2 mt-1">{creator.bio}</p>
      )}
      <Badge variant="outline" className="mt-2">{creator.artworks_count || 0} 作品</Badge>
    </CardContent>
  </Card>
);

export default SearchPage;
